import styled from "styled-components";
import { FLEX } from "styles/flex";
import { COLORS, DARK_THEME_COLORS, LIGHT_THEME_COLORS } from "styles/colors";

import { calcRem } from "styles/theme";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { commonLoginHeight } from "pages/auth/SignCommonStyled";
import { useEffect, useRef } from "react";

interface PhoneInputProps {
  login?: boolean;
  placeholder?: string;
  children?: any;
  selected?: string;
  onSelect?: (code: string) => void;
  onChange?: (phoneNumber: string) => void;
  disabled?: boolean;
  onEnterKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  isValidated?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  style?: React.CSSProperties;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  tabIndex?: number;
  "data-testid"?: string;
}

const Wrapper = styled.label<{
  disabled?: boolean;
  login?: boolean;
  isValidated?: boolean;
}>`
  ${FLEX.FlexStartCenter};
  width: 100%;
  height: 40px;
  border-radius: 2px;
  overflow: hidden;
  ${({ disabled, theme }) =>
    `
      border: 1px solid ${
        disabled ? theme.COLORS.DISABLED_BORDER : theme.COLORS.BORDER
      };
      background-color: ${disabled ? "transparent" : COLORS.WHITE};
      border-color: ${
        disabled ? theme.COLORS.DISABLED_BORDER : theme.COLORS.BORDER
      };
    `}

  ${({ login }) =>
    login &&
    `
      height: ${commonLoginHeight};
  `}

  .PhoneInput {
    height: 100%;
    width: 100%;
    .PhoneInputCountrySelectArrow {
      margin-top: -3px;
      margin-left: 3px;
    }
    input {
      appearance: none;
      border-radius: 0;
      height: 100%;
      padding: 0 8px;
      font-size: ${calcRem(14)};
      border: none;

      &::placeholder {
        color: #abb5c5;
      }

      &:disabled {
        background-color: ${({ theme }) => theme.COLORS.DISABLED};
        border-color: ${({ theme }) => theme.COLORS.DISABLED_BORDER};
        color: ${({ theme }) =>
          theme.value === "light"
            ? `${theme.COLORS.WHITE}60`
            : DARK_THEME_COLORS.LIGHTGRAY};
        cursor: "not-allowed";
      }
    }

    .PhoneInputCountry {
      padding-left: 14px;
      background-color: ${COLORS.WHITE};
      margin-right: 0;

      .PhoneInputCountryIcon {
        box-shadow: none;
        background: none;
        margin-top: -1px;
        padding-right: 3px;
      }

      .PhoneInputCountrySelect {
        z-index: unset;
      }

      ${({ disabled, theme }) =>
        disabled && {
          backgroundColor: theme.COLORS.DISABLED,
          borderColor: theme.COLORS.DISABLED_BORDER,
          color:
            theme.value === "light"
              ? `${theme.COLORS.WHITE}60`
              : DARK_THEME_COLORS.LIGHTGRAY,
          cursor: "not-allowed",
        }}
    }

    ${({ disabled, theme }) =>
      disabled &&
      `
        border-color: ${
          theme.value === "light"
            ? LIGHT_THEME_COLORS.BORDER
            : DARK_THEME_COLORS.BORDER
        }
    `}
    .PhoneInputCountry, .PhoneInputInput {
      background-color: transparent;
    }
    ${({ isValidated }) =>
      isValidated &&
      `
      border: 1px solid ${COLORS.VALIDATERED};
      background-color: #FFD5D5;
    `}
  }
`;

const PhoneInputComponent = ({
  login,
  placeholder,
  selected: value = "",
  onChange,
  disabled,
  isValidated,
  onKeyDown,
  style,
  onKeyUp,
  tabIndex,
  "data-testid": dataTestId = "atoms-input-phone-phone-input-string",
}: PhoneInputProps): JSX.Element => {
  const wrapperRef = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    // DOM이 렌더링된 후 country select의 tabindex를 -1로 설정
    if (wrapperRef.current) {
      const countrySelect = wrapperRef.current.querySelector(
        ".PhoneInputCountrySelect"
      );
      if (countrySelect) {
        (countrySelect as HTMLElement).setAttribute("tabindex", "-1");
      }
    }
  }, []);

  return (
    <Wrapper
      ref={wrapperRef}
      login={login}
      isValidated={isValidated}
      disabled={disabled}
      style={style}
    >
      <PhoneInput
        defaultCountry="US"
        countries={["US", "CA"]}
        initialValueFormat="national"
        limitMaxLength
        addInternationalOption={false}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={(phoneNumber) => onChange?.(String(phoneNumber ?? ""))}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        tabIndex={tabIndex}
        data-testid={dataTestId}
      />
    </Wrapper>
  );
};

export default PhoneInputComponent;
